import { useState, useRef, useEffect, useCallback } from 'react';

interface AnimationOptions {
  startValue: number;
  endValue: number;
  targetValue: number;
  duration?: number;
  shouldAnimate?: boolean;
  onComplete?: () => void;
}

export const useProgressAnimation = ({
  startValue,
  endValue,
  targetValue,
  duration = 600,
  shouldAnimate = true,
  onComplete,
}: AnimationOptions) => {
  const [animatedValue, setAnimatedValue] = useState(startValue);
  const [isRewardReady, setIsRewardReady] = useState(false);

  // Use refs to store stable values that won't change during the animation
  const animationRef = useRef<NodeJS.Timeout | null>(null);
  const isAnimationRunningRef = useRef(false);

  // Check if target is reached
  useEffect(() => {
    setIsRewardReady(endValue >= targetValue);
  }, [endValue, targetValue]);

  // Easing function for slow curve
  const easeInOutCubic = useCallback((t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }, []);

  // Function to animate progress growth
  const animateGrowth = useCallback(
    (
      start: number,
      end: number,
      duration: number,
      setterFn?: (value: number) => void,
      completeCallback?: () => void
    ) => {
      const startTime = Date.now();
      const endTime = startTime + duration;

      const updateValue = () => {
        const now = Date.now();
        const timeProgress = Math.min((now - startTime) / duration, 1);

        // Apply easing (slow curve)
        const easeProgress = easeInOutCubic(timeProgress);
        const currentAnimatedValue = start + (end - start) * easeProgress;

        // Use provided setter or default to internal state setter
        const setter = setterFn || setAnimatedValue;
        setter(Math.floor(currentAnimatedValue));

        if (now < endTime) {
          animationRef.current = setTimeout(updateValue, 16); // ~60fps
        } else {
          setter(end);
          if (completeCallback) completeCallback();
        }
      };

      updateValue();
    },
    [easeInOutCubic]
  );

  useEffect(() => {
    // Don't restart animation if already running
    if (isAnimationRunningRef.current) {
      return;
    }

    if (!shouldAnimate) {
      setAnimatedValue(endValue);
      return;
    }

    // Clear any existing animation
    if (animationRef.current) {
      clearTimeout(animationRef.current);
    }

    // Initialize state
    setAnimatedValue(startValue);

    // Add additional delay (500ms) to account for modal transition
    const modalTransitionDelay = 500;

    // Delay animation start
    const startDelay = setTimeout(() => {
      isAnimationRunningRef.current = true;

      // Animate progress growth
      animateGrowth(startValue, endValue, duration, undefined, () => {
        isAnimationRunningRef.current = false;
        if (onComplete) onComplete();
      });
    }, 200 + modalTransitionDelay);

    return () => {
      clearTimeout(startDelay);
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [startValue, endValue, targetValue, shouldAnimate, duration, animateGrowth, onComplete]);

  return { animatedValue, isRewardReady, animateGrowth };
};
